import React, { useState, useEffect, useCallback } from 'react';
import { getLicenseInfo, clearLicense, logout, isFreeTrial } from '../utils/licenseService';
import { isInstructorSeat } from '../lib/seatRole';

const LicenseStatusBadge = ({ onLogout }) => {
  const [remaining, setRemaining] = useState(null);
  const [licenseInfo, setLicenseInfo] = useState(null);

  const calculateRemaining = useCallback(() => {
    const info = getLicenseInfo();
    if (!info || !info.expiresAt) {
      setRemaining(null);
      setLicenseInfo(null);
      return;
    }

    setLicenseInfo(info);
    const now = new Date().getTime();
    const expires = new Date(info.expiresAt).getTime();
    const diff = expires - now;

    if (diff <= 0) {
      clearLicense();
      window.location.reload();
      return;
    }

    setRemaining(diff);
  }, []);

  useEffect(() => {
    calculateRemaining();
    const interval = setInterval(calculateRemaining, 1000);
    return () => clearInterval(interval);
  }, [calculateRemaining]);

  const handleLogout = () => {
    logout();
    if (onLogout) {
      onLogout();
    } else {
      window.location.reload();
    }
  };

  if (remaining === null || !licenseInfo) {
    return null;
  }

  // 무료 체험 모드
  if (isFreeTrial()) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium
          bg-emerald-900/40 border-emerald-700/50 text-emerald-200">
          <span className="w-2 h-2 rounded-full flex-shrink-0 bg-emerald-400" />
          <span>무료 체험 중</span>
          <span className="opacity-50">|</span>
          <span>Day 1</span>
        </div>
        <a
          href="https://semifabai.com/missions"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium
            bg-blue-700/40 border-blue-600/50 text-blue-200
            hover:bg-blue-600/60 hover:border-blue-500/70
            transition-colors duration-200 no-underline"
          title="면접 미션 보기 — 개인 상품"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
          </svg>
          면접 미션 보기
        </a>
        {/* 체험 모드에서도 로그아웃(코드 입력) 가능 */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium
            bg-gray-700/40 border-gray-600/50 text-gray-300
            hover:bg-red-900/40 hover:border-red-700/50 hover:text-red-200
            transition-colors duration-200"
          title="코드 입력으로 전환"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
          </svg>
          코드 입력
        </button>
      </div>
    );
  }

  // 시간 분해
  const totalSeconds = Math.floor(remaining / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n) => String(n).padStart(2, '0');
  const timeStr = `${days}일 ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

  // 색상 결정
  const oneDay = 24 * 60 * 60 * 1000;
  const oneHour = 60 * 60 * 1000;

  let dotColor, textColor, bgColor, borderColor, animate;

  if (remaining > oneDay) {
    dotColor = 'bg-green-400';
    textColor = 'text-green-100';
    bgColor = 'bg-green-900/40';
    borderColor = 'border-green-700/50';
    animate = '';
  } else if (remaining > oneHour) {
    dotColor = 'bg-yellow-400';
    textColor = 'text-yellow-100';
    bgColor = 'bg-yellow-900/40';
    borderColor = 'border-yellow-700/50';
    animate = '';
  } else {
    dotColor = 'bg-red-500';
    textColor = 'text-red-100';
    bgColor = 'bg-red-900/40';
    borderColor = 'border-red-700/50';
    animate = 'animate-pulse';
  }

  const typeName = licenseInfo.typeName || `${licenseInfo.daysTotal || '?'}일 이용권`;

  return (
    <div className="flex items-center gap-2">
      <ModeBadge licenseInfo={licenseInfo} />
      <ClassAccessTag licenseInfo={licenseInfo} />
      {/* 이용권 상태 배지 */}
      <div
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium
          ${bgColor} ${borderColor} ${textColor} ${animate}`}
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
        <span>{typeName}</span>
        <span className="opacity-50">|</span>
        <span className="font-mono tabular-nums">{timeStr}</span>
      </div>

      {/* 내 코드 — QR 로 들어온 학생은 코드를 받아 적을 새가 없다. 기기를 옮길 때
          여기서 다시 찾을 수 있어야 노트북에서 주소를 다시 열어 자리를 또 먹지 않는다. */}
      <SeatCodeBadge licenseInfo={licenseInfo} />

      {/* 로그아웃 버튼 */}
      <button
        onClick={handleLogout}
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium
          bg-gray-700/40 border-gray-600/50 text-gray-300
          hover:bg-red-900/40 hover:border-red-700/50 hover:text-red-200
          transition-colors duration-200"
        title="로그아웃"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
        </svg>
        로그아웃
      </button>
    </div>
  );
};

// 지금 어느 모드인가 — 같은 화면, 같은 코드로 들어오는데 보이는 것이 다르다.
// 강의자에게는 담기가 보이고 학생에게는 안 보인다. 그 차이를 화면이 말하지 않으면
// 강의자는 "왜 나만 다르지", 학생은 "왜 나는 담기가 없지"가 된다.
//
// 개인 이용권에는 붙이지 않는다 — 가를 것이 없는데 배지를 달면 그냥 소음이다(규칙 8).
function ModeBadge({ licenseInfo }) {
  if (!licenseInfo?.isInstitution) return null;
  const teaching = isInstructorSeat(licenseInfo);
  return (
    <span
      className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-bold tracking-wide
        ${teaching
        ? 'bg-amber-900/40 border-amber-600/50 text-amber-200'
        : 'bg-sky-900/40 border-sky-700/50 text-sky-200'}`}
      title={teaching
        ? '이 코드는 강의자용입니다 — 수업 자료를 담을 수 있고, 학생 통계에는 들어가지 않습니다.'
        : '이 코드는 학생용입니다 — 강의자가 연 자료를 봅니다.'}
    >
      {teaching ? (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 5h18v11H3z" /><path d="M12 16v3M8 19h8" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8l9-4 9 4-9 4-9-4Z" /><path d="M7 10.5V15c0 1.1 2.2 2 5 2s5-.9 5-2v-4.5" />
        </svg>
      )}
      {teaching ? '강의 모드' : '학생 모드'}
    </span>
  );
}

// 기관이 연 반(class)으로 들어온 접속이라는 표시 — 기관 반은 전부 강의·교육 목적이다(담당자 2026-10-14).
// 이용권 상태(남은 시간) 바로 앞에 붙는다. 기관 좌석이면 학생·강의자 모두, 개인 이용권·무료 체험에는 없다.
// 모드 배지(학생/강의)와 달리 좁은 화면에서도 숨기지 않는다 — 접속의 성격을 밝히는 표시라 늘 보여야 한다.
function ClassAccessTag({ licenseInfo }) {
  if (!licenseInfo?.isInstitution) return null;
  return (
    <span
      className="flex items-center px-2 py-1.5 rounded-lg border text-[11px] font-bold tracking-wide whitespace-nowrap
        bg-indigo-900/40 border-indigo-600/50 text-indigo-200"
      title="기관이 연 반(class)으로 들어온 접속입니다 — 강의·교육 목적"
    >
      Class Access
    </span>
  );
}

// 좌석 코드를 눌러 복사. 기관 좌석에만 붙는다 — 개인 이용권은 코드를 스스로 알고 있다.
//
// ⚠️ 강의자 좌석은 **기본으로 가린다.** 이 배지는 수업 내내 헤더에 떠 있고, 강의자 화면은
//    대개 빔 프로젝터에 물려 있다. 입력칸만 가리고 여기를 그대로 두면 가린 것이 아니다 —
//    노출 시간은 오히려 이쪽이 길다(코드 한 번 치는 몇 초 vs 수업 전체).
//    학생 좌석은 종전 그대로 둔다 — 자기 화면이고, 기기를 옮길 때 바로 찾아야 한다.
//    펼친 뒤에는 **스스로 다시 접는다.** 펼쳐 놓고 잊으면 결국 같은 노출이라 그걸 사람
//    기억에 맡길 수 없다.
const SEAT_CODE_REVEAL_MS = 20000;

function SeatCodeBadge({ licenseInfo }) {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const code = licenseInfo?.isInstitution ? licenseInfo.code : null;
  const hidden = isInstructorSeat(licenseInfo) && !revealed;

  // 펼쳐 둔 강의자 코드는 시간이 지나면 다시 접는다.
  useEffect(() => {
    if (!revealed) return undefined;
    const t = setTimeout(() => setRevealed(false), SEAT_CODE_REVEAL_MS);
    return () => clearTimeout(t);
  }, [revealed]);

  if (!code) return null;

  const copy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }).catch(() => window.prompt('코드 복사:', code));
    } else {
      window.prompt('코드 복사:', code);
    }
  };

  // 가려져 있으면 첫 누름은 **펼치기**다. 보지도 않은 것을 복사하게 하면
  // 강의자는 무엇을 옮겼는지 모른 채 붙여넣는다.
  const onClick = () => (hidden ? setRevealed(true) : copy());

  return (
    <button
      onClick={onClick}
      aria-label={hidden ? '내 코드 보기' : '내 코드 복사'}
      title={hidden
        ? '내 코드 — 눌러서 봅니다. 강의 화면을 함께 보고 있다면 주의하세요'
        : '내 코드 — 다른 기기에서 이 코드로 들어오면 같은 자리입니다'}
      className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium
        bg-slate-800/60 border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white transition-colors"
    >
      {hidden ? (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" />
        </svg>
      )}
      <span className="font-mono">{hidden ? '내 코드' : copied ? '복사됨' : code}</span>
    </button>
  );
}

export default LicenseStatusBadge;
